import {
  Alert,
  Box,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useKundmarkeContext } from '../../../common/contexts/KundmarkeContext';
import { FormError } from '../../../types/FormError';
import { LoadState } from '../../../types/LoadState';
import * as varderingClient from '../../clients/VarderingClient';
import { createVarderingParametrar } from '../../lib/createVarderingParametrar';
import { getEnabledParameters } from '../../lib/getEnabledParameters';
import {
  BostadsinformationDto,
  BostadsvarderingDto,
  Kategori,
  VarderingParametrarDto,
} from '../../types/dtos/VarderingarModel';
import { Adress } from './Adress';
import { ValidationMessage } from './ValidationMessage';
import { VarderingInput } from './VarderingInput';
import { VarderaButton } from './VarderaButton';
import { ApiError } from '../../../types/ApiError';
import { ApiResponse } from '../../../types/ApiResponse';
import {
  ServerValidationError,
  isCustomValidationError,
  validationErrorParser,
} from '../../lib/validationErrorParser';

const toFieldMap = (
  errors: ServerValidationError[]
): VarderingValidationErrors =>
  errors.reduce(
    (acc, current) => ({
      ...acc,
      [current.field]: current.text,
    }),
    {} as VarderingValidationErrors
  );

interface VarderingFormProps {
  bostadInformation: BostadsinformationDto;
  activeVardering: BostadsvarderingDto;
  setActiveVardering: (bostadsvardering: BostadsvarderingDto) => void;
}

type VarderingValidationErrors = Partial<{
  [key in keyof VarderingParametrarDto]: string;
}>;

export const VarderingForm: React.FC<VarderingFormProps> = ({
  bostadInformation,
  setActiveVardering,
  activeVardering,
}) => {
  const [varderingsParametrar, setVarderingsParametrar] =
    useState<VarderingParametrarDto>(
      createVarderingParametrar(bostadInformation, activeVardering)
    );
  const { kundmarke } = useKundmarkeContext();

  const [enabledParameters, setEnabledParameters] = useState(
    getEnabledParameters(varderingsParametrar?.kategori)
  );
  const [buyLoadState, setBuyLoadState] = useState(LoadState.Idle);
  const [error, setError] = useState<FormError>();
  const [validationErrors, setValidationErrors] =
    useState<VarderingValidationErrors>({});

  const setVarderingsParametrarValue = (
    key: keyof VarderingParametrarDto,
    value: string
  ): void => {
    setValidationErrors({ ...validationErrors, [key]: undefined });
    setVarderingsParametrar({ ...varderingsParametrar, [key]: value });
  };

  const isOmvardering = (vardering: BostadsvarderingDto): boolean =>
    vardering?.omvardering?.antalOmvarderingar > -1;

  const performVardering = async (
    varderingsParametrar: VarderingParametrarDto,
    id?: string,
    kundmarke?: string
  ) => {
    setBuyLoadState(LoadState.Loading);
    setError(undefined);
    const response = await (id
      ? varderingClient.createOmvardering(id, varderingsParametrar, kundmarke)
      : varderingClient.createVardering(varderingsParametrar, kundmarke));
    if (response.success) {
      setBuyLoadState(LoadState.Success);
      return setActiveVardering(response.data);
    }
    setBuyLoadState(LoadState.Error);
    if (response.statusCode === 400) {
      const errorResponse = response as unknown as ApiResponse<ApiError>;
      if (isCustomValidationError(errorResponse)) {
        setValidationErrors(toFieldMap(validationErrorParser(errorResponse)));
      } else {
        setError({
          severity: 'error',
          text: `Värderingen misslyckades. Se över dina parametrar och försök igen.`,
        });
      }
    } else {
      setError({
        severity: 'error',
        text: `Värderingen misslyckades. Försök igen.`,
      });
    }
  };

  useEffect(() => {
    if (bostadInformation) {
      setVarderingsParametrar(
        createVarderingParametrar(bostadInformation, activeVardering)
      );
    }
  }, [bostadInformation]);

  useEffect(
    () =>
      setEnabledParameters(getEnabledParameters(varderingsParametrar.kategori)),
    [varderingsParametrar]
  );

  const onSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault();
    performVardering(
      varderingsParametrar,
      isOmvardering(activeVardering) ? activeVardering.id : undefined,
      kundmarke
    );
  };

  return (
    <form onSubmit={onSubmit}>
      <Stack spacing={2}>
        <ValidationMessage agandeform={bostadInformation?.agandeform} />
        <Adress bostadsinfo={bostadInformation} />
        <Typography variant="h6">Bostadsuppgifter</Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          Det är viktigt att ange så många uppgifter som möjligt för att
          bostadsvärderingen ska bli bra.
          <br />
          Mer information finns på{' '}
          <Link
            underline="hover"
            href="https://metria.se/metria-fastighets%C3%B6k-support-fastighets%C3%B6k-webb-bostadsv%C3%A4rdering"
            target="_blank"
          >
            Hjälpsidan
          </Link>
          .
        </Alert>
        {error && <Alert severity={error.severity}>{error.text}</Alert>}
        <Box sx={{ pt: 1 }}>
          <Grid container spacing={2} columns={5} sx={{ width: 800 }}>
            <Grid item xs={2}>
              <FormControl
                fullWidth
                size="small"
                required
                error={!!validationErrors.kategori}
                sx={{ width: '220px' }}
              >
                <InputLabel id="demo-simple-select-label">
                  Bostadskategori
                </InputLabel>
                <Select<Kategori | ''>
                  value={varderingsParametrar.kategori || ''}
                  label="Bostadskategori"
                  onChange={(e) =>
                    setVarderingsParametrarValue('kategori', e.target.value)
                  }
                  disabled={
                    isOmvardering(activeVardering) ||
                    !bostadInformation ||
                    bostadInformation.id === 0
                  }
                >
                  <MenuItem value={Kategori.BOSTADSRATT}>Bostadsrätt</MenuItem>
                  <MenuItem value={Kategori.SMAHUS}>Småhus</MenuItem>
                </Select>
                {validationErrors.kategori ? (
                  <FormHelperText>{validationErrors.kategori}</FormHelperText>
                ) : null}
              </FormControl>
            </Grid>
            <Grid item xs={3} />
            <VarderingInput
              label="Boarea"
              value={varderingsParametrar.boarea}
              onChange={(val) => setVarderingsParametrarValue('boarea', val)}
              enabled={enabledParameters.boarea}
              required
              error={validationErrors.boarea}
            />
            <VarderingInput
              label="Biarea"
              value={varderingsParametrar.biarea}
              onChange={(val) => setVarderingsParametrarValue('biarea', val)}
              enabled={enabledParameters.biarea}
              error={validationErrors.biarea}
            />
            <VarderingInput
              label="Tomtarea"
              value={varderingsParametrar.tomtarea}
              onChange={(val) => setVarderingsParametrarValue('tomtarea', val)}
              enabled={enabledParameters.tomt}
              error={validationErrors.tomtarea}
            />
            <VarderingInput
              label="Byggår"
              value={varderingsParametrar.byggar}
              onChange={(val) => setVarderingsParametrarValue('byggar', val)}
              enabled={enabledParameters.byggar}
              error={validationErrors.byggar}
            />
            <VarderingInput
              label="Standardpoäng"
              value={varderingsParametrar.standardpoang}
              onChange={(val) =>
                setVarderingsParametrarValue('standardpoang', val)
              }
              enabled={enabledParameters.standardpoang}
              error={validationErrors.standardpoang}
            />
            <VarderingInput
              label="Antal rum"
              value={varderingsParametrar.antalRum}
              onChange={(val) => setVarderingsParametrarValue('antalRum', val)}
              enabled={enabledParameters.antalRum}
              error={validationErrors.antalRum}
            />
            <VarderingInput
              enabled={enabledParameters.avgift}
              label="Avgift"
              value={varderingsParametrar.avgift}
              onChange={(val) => setVarderingsParametrarValue('avgift', val)}
              error={validationErrors.avgift}
            />
            <VarderingInput
              enabled={enabledParameters.vaning}
              label="Våning"
              value={varderingsParametrar.vaning}
              onChange={(val) => setVarderingsParametrarValue('vaning', val)}
              error={validationErrors.vaning}
            />
            <VarderingInput
              enabled={enabledParameters.antalVaningar}
              label="Antal våningar"
              value={varderingsParametrar.antalVaningar}
              onChange={(val) =>
                setVarderingsParametrarValue('antalVaningar', val)
              }
              error={validationErrors.antalVaningar}
            />
          </Grid>
        </Box>
        <Divider sx={{ pt: 1 }} />
        <VarderaButton
          hasBostadsinformation={!!bostadInformation}
          omvardering={activeVardering?.omvardering}
          loadState={buyLoadState}
        />
      </Stack>
    </form>
  );
};
